import { Link } from "react-router-dom";
import { Spark } from "../components/Spark";
import { SITE_NAME } from "../constants/site";
import { ArrowLeft } from "lucide-react";

function LegalShell({ titulo, children }) {
  return (
    <div className="dark min-h-screen bg-background text-foreground">
      <header className="border-b border-border/60">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5">
            <Spark className="h-7 w-7" />
            <span className="font-bold tracking-tight text-sm">{SITE_NAME}</span>
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-10 space-y-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Voltar
        </Link>
        <h1 className="text-3xl font-bold tracking-tight">{titulo}</h1>
        <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">{children}</div>
      </main>
      <footer className="border-t border-border/60">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-6 flex items-center gap-4 text-xs text-muted-foreground">
          <Link to="/termos" className="hover:text-foreground transition-colors">Termos de Uso</Link>
          <Link to="/privacidade" className="hover:text-foreground transition-colors">Privacidade</Link>
          <a href="https://wa.me/5531990682809" target="_blank" rel="noreferrer" className="hover:text-foreground transition-colors">Contato</a>
        </div>
      </footer>
    </div>
  );
}

function Sec({ n, titulo, children }) {
  return (
    <section className="space-y-2">
      <h2 className="text-base font-bold tracking-tight text-foreground">{n}. {titulo}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

export function Termos() {
  return (
    <LegalShell titulo="Termos de Uso">
      <p><strong className="text-foreground">Última atualização: julho de 2026.</strong></p>
      <p>
        Estes termos explicam, em linguagem simples, as regras de uso do <strong className="text-foreground">{SITE_NAME}</strong> —
        uma plataforma de estudo com inteligência artificial que personaliza o aprendizado a partir do perfil de cada aluno.
        Ao criar uma conta, você concorda com o que está escrito aqui.
      </p>

      <Sec n="1" titulo="O que é o serviço">
        <p>
          O {SITE_NAME} oferece chat de estudo com IA, Modo Prova, quizzes, flashcards com revisão espaçada,
          upload de material, rotina de estudos e um perfil do aluno que personaliza as respostas do tutor.
        </p>
        <p>
          <strong className="text-foreground">Importante:</strong> as respostas da IA são apoio ao estudo e podem conter erros.
          Elas não substituem seus professores nem seu material oficial — na dúvida, o material da sua escola é quem manda.
        </p>
      </Sec>

      <Sec n="2" titulo="Sua conta">
        <p>
          Pra usar a plataforma você cria uma conta com nome, e-mail e senha. A senha é sua responsabilidade:
          não compartilhe com ninguém. Tudo que acontece na sua conta é atribuído a você.
        </p>
        <p>
          Você deve fornecer informações verdadeiras. Uma pessoa = uma conta.
        </p>
      </Sec>

      <Sec n="3" titulo="Menores de idade">
        <p>
          A plataforma é feita pra estudantes, e isso inclui menores de 18 anos. Se você é menor, ao criar a conta você declara
          que um responsável (pai, mãe ou tutor legal) está ciente e de acordo com o uso da plataforma e com estes termos.
          O responsável pode, a qualquer momento, pedir informações sobre a conta ou a exclusão dela pelo nosso contato.
        </p>
      </Sec>

      <Sec n="4" titulo="Planos, pagamento e cancelamento">
        <p>
          O plano Grátis é gratuito de verdade, com limites diários de uso. Os planos pagos (Médio e Pro) liberam mais recursos
          e valem por 30 dias a partir da ativação.
        </p>
        <p>
          Se um plano pago não for renovado, a conta volta automaticamente pro Grátis — sem multa, sem cobrança surpresa
          e <strong className="text-foreground">sem perder seu histórico nem seu perfil</strong>. Você pode parar de usar quando quiser.
        </p>
        <p>
          Preços e limites de cada plano ficam sempre visíveis na página de planos e podem ser ajustados — mudanças nunca
          reduzem um período pago que já está em andamento.
        </p>
      </Sec>

      <Sec n="5" titulo="Uso aceitável">
        <p>Ao usar a plataforma, você concorda em NÃO:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>usar o serviço pra colar em provas ou violar regras da sua escola;</li>
          <li>enviar conteúdo ilegal, ofensivo ou que viole direitos de terceiros;</li>
          <li>tentar acessar dados de outros alunos ou burlar as proteções da plataforma;</li>
          <li>usar robôs/automações pra abusar dos limites de uso.</li>
        </ul>
        <p>Contas que violarem essas regras podem ser suspensas ou encerradas.</p>
      </Sec>

      <Sec n="6" titulo="Seu conteúdo">
        <p>
          O que você cria e envia (conversas, materiais, perfil, quizzes, flashcards) continua sendo seu.
          Você nos dá permissão apenas pra processar esse conteúdo com o objetivo de fazer o serviço funcionar pra você —
          nada além disso. Os detalhes de privacidade estão na nossa <Link to="/privacidade" className="text-violet-400 hover:text-violet-300 underline underline-offset-2">Política de Privacidade</Link>.
        </p>
      </Sec>

      <Sec n="7" titulo="Disponibilidade e limites de responsabilidade">
        <p>
          Trabalhamos pra manter o serviço no ar, mas ele pode ficar indisponível por manutenção ou por falhas de terceiros
          (hospedagem, provedores de IA). O serviço é fornecido "como está": não garantimos nota, aprovação nem resultado
          específico — o estudo continua sendo seu. 😉
        </p>
      </Sec>

      <Sec n="8" titulo="Encerramento e exclusão da conta">
        <p>
          Você pode pedir a exclusão da sua conta e de todos os seus dados a qualquer momento pelo nosso contato
          (link no rodapé). A exclusão é definitiva.
        </p>
      </Sec>

      <Sec n="9" titulo="Mudanças nestes termos">
        <p>
          Se estes termos mudarem de forma relevante, avisaremos na plataforma. A data da última atualização fica sempre
          no topo desta página.
        </p>
      </Sec>

      <Sec n="10" titulo="Contato">
        <p>
          Dúvidas sobre estes termos: fale com a gente pelo WhatsApp no rodapé desta página.
        </p>
      </Sec>
    </LegalShell>
  );
}

export function Privacidade() {
  return (
    <LegalShell titulo="Política de Privacidade">
      <p><strong className="text-foreground">Última atualização: julho de 2026.</strong></p>
      <p>
        Esta política explica, em português claro, como o <strong className="text-foreground">{SITE_NAME}</strong> trata
        seus dados, em linha com a <strong className="text-foreground">LGPD</strong> (Lei Geral de Proteção de Dados — Lei 13.709/2018).
        O resumo honesto: coletamos o mínimo necessário, usamos só pra personalizar o SEU estudo, e <strong className="text-foreground">não
        vendemos nem compartilhamos seus dados com ninguém</strong>.
      </p>

      <Sec n="1" titulo="Quais dados coletamos">
        <ul className="list-disc pl-5 space-y-1">
          <li><strong className="text-foreground">Cadastro:</strong> nome, e-mail e senha (a senha é guardada criptografada — nem a gente consegue ler).</li>
          <li><strong className="text-foreground">Perfil de estudo:</strong> o que você mesmo conta pro tutor — como aprende melhor, o que te trava, objetivos, matérias com dificuldade, tom preferido e o campo livre.</li>
          <li><strong className="text-foreground">Conteúdo de estudo:</strong> suas conversas com o tutor, materiais enviados, quizzes, flashcards, rotina e resultados.</li>
          <li><strong className="text-foreground">Uso:</strong> contadores simples (quantas mensagens e quizzes por dia) pra aplicar os limites de cada plano.</li>
        </ul>
        <p>Não coletamos localização, contatos, nem dados do seu aparelho além do necessário pro app funcionar.</p>
      </Sec>

      <Sec n="2" titulo="Pra que usamos">
        <p>Uma finalidade só: <strong className="text-foreground">fazer o seu tutor funcionar e ficar melhor pra VOCÊ</strong>. Na prática:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>responder suas dúvidas e gerar seus quizzes, flashcards e planos;</li>
          <li>personalizar as explicações com base no seu perfil e nos seus erros;</li>
          <li>aplicar os limites do seu plano e manter sua conta segura.</li>
        </ul>
        <p>
          <strong className="text-foreground">O que NUNCA fazemos:</strong> vender seus dados, compartilhar com anunciantes,
          usar seu perfil pra publicidade, ou mostrar os dados de um aluno pra outro.
        </p>
      </Sec>

      <Sec n="3" titulo="Seu perfil de estudo é privado">
        <p>
          O perfil do aluno é o dado mais pessoal da plataforma — e é tratado assim. Só a sua conta autenticada acessa o seu perfil;
          nenhum outro aluno vê. Ele é usado exclusivamente pra personalizar as respostas do SEU tutor, e nada mais.
        </p>
      </Sec>

      <Sec n="4" titulo="IA e operadores">
        <p>
          Pra gerar as respostas, suas mensagens e trechos do seu material são processados por provedores de inteligência artificial
          e de infraestrutura (servidores e banco de dados), que atuam como operadores sob nossas instruções.
          <strong className="text-foreground"> Seus dados não são usados pra treinar modelos de IA.</strong>
        </p>
      </Sec>

      <Sec n="5" titulo="Como protegemos">
        <ul className="list-disc pl-5 space-y-1">
          <li>Conexão criptografada (HTTPS) entre você e a plataforma.</li>
          <li>Senha guardada com criptografia forte (hash bcrypt) — nunca em texto puro.</li>
          <li>Acesso aos seus dados sempre protegido por autenticação (token de login).</li>
          <li>Cada aluno só acessa os próprios dados — isso é verificado no servidor, em toda requisição.</li>
        </ul>
      </Sec>

      <Sec n="6" titulo="Menores de idade">
        <p>
          A plataforma atende estudantes, incluindo menores de 18 anos. Nesses casos, o uso pressupõe ciência do responsável legal,
          como previsto nos <Link to="/termos" className="text-violet-400 hover:text-violet-300 underline underline-offset-2">Termos de Uso</Link>.
          O responsável pode solicitar acesso, correção ou exclusão dos dados do menor pelo nosso contato.
        </p>
      </Sec>

      <Sec n="7" titulo="Seus direitos (LGPD)">
        <p>Você (ou seu responsável) pode, a qualquer momento e de graça:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>saber quais dados temos sobre você e pedir uma cópia;</li>
          <li>corrigir dados incorretos;</li>
          <li><strong className="text-foreground">apagar sua conta e todos os seus dados</strong> (exclusão definitiva);</li>
          <li>revogar consentimentos.</li>
        </ul>
        <p>É só chamar no contato do rodapé — respondemos o quanto antes.</p>
      </Sec>

      <Sec n="8" titulo="Por quanto tempo guardamos">
        <p>
          Enquanto sua conta existir. Se você pedir a exclusão, apagamos seus dados dos nossos sistemas.
          Registros técnicos mínimos podem ser mantidos pelo tempo exigido por lei.
        </p>
      </Sec>

      <Sec n="9" titulo="Mudanças nesta política">
        <p>
          Se algo relevante mudar, avisaremos na plataforma. A data da última atualização fica no topo desta página.
        </p>
      </Sec>

      <Sec n="10" titulo="Contato">
        <p>
          Pra exercer seus direitos ou tirar dúvidas sobre privacidade, fale com a gente pelo WhatsApp no rodapé desta página.
        </p>
      </Sec>
    </LegalShell>
  );
}
